import forIn from 'lodash/forIn';

import { setDefaultVolume, setDefaultMutedState } from './settings';

// The event list that follows needs to be the same for all platforms

const trackingEvents = {
  EVENT_READY: 'READY',
  EVENT_STARTED: 'START',
  EVENT_ACTIVE_VIEW: 'ACTIVE_VIEW',
  EVENT_MUTED: 'MUTE',
  EVENT_UNMUTED: 'UNMUTE',
  EVENT_FULLSCREEN_STARTED: 'FULLSCREEN_START',
  EVENT_FULLSCREEN_EXITED: 'FULLSCREEN_EXIT',
  EVENT_WATCHED_3_SECONDS: 'THREE_SECONDS',
  EVENT_WATCHED_10_SECONDS: 'TEN_SECONDS',
  EVENT_WATCHED_25_PERCENT: 'TWENTY_FIVE_PERCENT',
  EVENT_WATCHED_50_PERCENT: 'FIFTY_PERCENT',
  EVENT_WATCHED_75_PERCENT: 'SEVENTY_FIVE_PERCENT',
  EVENT_WATCHED_95_PERCENT: 'NINETY_FIVE_PERCENT',
  EVENT_WATCHED_100_PERCENT: 'ONE_HUNDRED_PERCENT',
  EVENT_SETUP_ERROR: 'SETUP_ERROR',
  EVENT_PLAYER_ERROR: 'PLAYER_ERROR',
};

const nonTrackingEvents = {
  EVENT_PLAY: 'play',
  EVENT_PAUSE: 'pause',
};

const events = {
  ...trackingEvents,
  ...nonTrackingEvents,
};

// key is the second watched; value is xing event name
const secondsWatchedEventList = {
  3: events.EVENT_WATCHED_3_SECONDS,
  10: events.EVENT_WATCHED_10_SECONDS,
};

// key is the percentage watched; value is xing event name
const percentageWatchedEventList = {
  25: events.EVENT_WATCHED_25_PERCENT,
  50: events.EVENT_WATCHED_50_PERCENT,
  75: events.EVENT_WATCHED_75_PERCENT,
  95: events.EVENT_WATCHED_95_PERCENT,
};

class EventHandler {
  constructor(
    xingPlayer,
    section,
    extraFields,
    eventsHandler,
    trackEventMutation
  ) {
    this.xingPlayer = xingPlayer;
    this.eventsHandler = eventsHandler;
    this.trackEventMutation = trackEventMutation;
    this.section = section;
    this.extraFields = extraFields;
    this.inViewTimer = null;
    this.triggerPercentageEventsList = {};
    this.triggerSecondsWatchedEventsList = {};
    this.lastSecondChecked = null;
    this.init();
  }

  init() {
    // trigger ready event
    this.triggerEvent(trackingEvents.EVENT_READY);

    // init event handler
    this.xingPlayer.videojs.on('firstplay', () => this.firstplayHandler());
    this.xingPlayer.videojs.on('play', () => this.playHandler());
    this.xingPlayer.videojs.on('pause', () => this.pauseHandler());
    this.xingPlayer.videojs.on('timeupdate', () => this.timeUpdateHandler());
    this.xingPlayer.videojs.on('durationchange', () =>
      this.durationChangeHandler()
    );
    this.xingPlayer.videojs.on('volumechange', () =>
      this.volumeChangeHandler()
    );
    this.xingPlayer.videojs.on('fullscreenchange', () =>
      this.fullscreenChangeHandler()
    );
    this.xingPlayer.videojs.on('ended', () => this.endedHandler());
  }

  triggerEvent(event) {
    this.triggerTrackingEvent(event);
    this.triggerXingVideoEvent(event);
  }

  triggerTrackingEvent(event) {
    const data = {
      collection: [
        {
          type: event,
          siteSection: this.section,
          clientTimestamp: new Date(),
          videoRef: this.xingPlayer.videoId,
          extraFields: JSON.stringify(this.extraFields || {}),
        },
      ],
    };

    this.trackEventMutation({
      variables: { input: data },
    });
  }

  triggerXingVideoEvent(event) {
    switch (event) {
      case events.EVENT_READY:
        this.eventsHandler.onReady && this.eventsHandler.onReady();
        break;
      case events.EVENT_STARTED:
        this.eventsHandler.onStart && this.eventsHandler.onStart();
        break;
      case events.EVENT_MUTED:
        this.eventsHandler.onMute && this.eventsHandler.onMute(true);
        break;
      case events.EVENT_UNMUTED:
        this.eventsHandler.onMute && this.eventsHandler.onMute(false);
        break;
      case events.EVENT_PLAY:
        this.eventsHandler.onPlay && this.eventsHandler.onPlay();
        break;
      case events.EVENT_PAUSE:
        this.eventsHandler.onPause && this.eventsHandler.onPause();
        break;
      case events.EVENT_WATCHED_100_PERCENT:
        this.eventsHandler.onComplete && this.eventsHandler.onComplete();
        break;
      case events.EVENT_SETUP_ERROR || events.EVENT_PLAYER_ERROR:
        this.eventsHandler.onError && this.eventsHandler.onError();
        break;
    }
  }

  // Events Handler Functions
  firstplayHandler() {
    this.triggerEvent(trackingEvents.EVENT_STARTED);
    this.checkVideoActivity();
  }

  playAgainHandler() {
    this.triggerEvent(trackingEvents.EVENT_STARTED);
    this.xingPlayer.activeTrackingSent = false;
    this.xingPlayer.ended = false;
  }

  playHandler() {
    if (this.xingPlayer.ended) {
      this.playAgainHandler();
    }
    this.checkVideoActivity();
    if (
      !this.xingPlayer.videojs.seeking() &&
      !this.xingPlayer.videojs.ended()
    ) {
      this.triggerXingVideoEvent(events.EVENT_PLAY);
    }
    this.playToogleHandler();
  }

  pauseHandler() {
    if (
      !this.xingPlayer.videojs.seeking() &&
      !this.xingPlayer.videojs.ended()
    ) {
      this.triggerXingVideoEvent(events.EVENT_PAUSE);
    }
    this.playToogleHandler();
  }

  playToogleHandler() {
    const bigPlayButton = this.xingPlayer.videojs
      .el()
      .querySelector('.vjs-big-play-button');
    bigPlayButton.classList.add('xing-vjs-changing-status');
    setTimeout(
      () => bigPlayButton.classList.remove('xing-vjs-changing-status'),
      500
    );
  }

  checkVideoActivity() {
    if (
      !this.xingPlayer.activeTrackingSent &&
      this.xingPlayer.inView &&
      !this.xingPlayer.videojs.muted() &&
      !this.xingPlayer.videojs.paused()
    ) {
      this.triggerEvent(trackingEvents.EVENT_ACTIVE_VIEW);
      this.xingPlayer.activeTrackingSent = true;
    }
  }

  volumeChangeHandler() {
    const previousMuteState = this.xingPlayer.isMuted;
    const muted = this.xingPlayer.videojs.muted();
    const volume = this.xingPlayer.videojs.volume();

    // save user volume preferences
    setDefaultVolume(volume);

    // videojs does not have a mute event so we need to implement it with volumechange
    if (previousMuteState !== muted) {
      this.xingPlayer.isMuted = muted;
      this.triggerEvent(
        muted ? trackingEvents.EVENT_MUTED : trackingEvents.EVENT_UNMUTED
      );

      // save user muted preferences
      setDefaultMutedState(muted);

      !muted && this.checkVideoActivity();
    }
  }

  handleIntersection() {
    if (this.xingPlayer.inView) {
      this.checkVideoActivity(this.xingPlayer);
    }
  }

  fullscreenChangeHandler() {
    this.triggerEvent(
      this.xingPlayer.videojs.isFullscreen()
        ? trackingEvents.EVENT_FULLSCREEN_STARTED
        : trackingEvents.EVENT_FULLSCREEN_EXITED
    );
  }

  timeUpdateHandler() {
    const time = Math.floor(this.xingPlayer.videojs.currentTime());

    if (
      this.lastSecondChecked !== time &&
      this.triggerPercentageEventsList[time]
    ) {
      this.triggerEvent(this.triggerPercentageEventsList[time]);
    }
    if (
      this.lastSecondChecked !== time &&
      this.triggerSecondsWatchedEventsList[time]
    ) {
      this.triggerEvent(this.triggerSecondsWatchedEventsList[time]);
    }

    this.lastSecondChecked = time;
  }

  durationChangeHandler() {
    const videoDuration = this.xingPlayer.videojs.duration();

    this.triggerSecondsWatchedEventsList = { ...secondsWatchedEventList };

    forIn(percentageWatchedEventList, (value, key) => {
      this.triggerPercentageEventsList[
        Math.floor((videoDuration * key) / 100)
      ] = value;
    });
  }

  endedHandler() {
    this.xingPlayer.ended = true;
    this.triggerEvent(trackingEvents.EVENT_WATCHED_100_PERCENT);
  }
}

export default EventHandler;
